import { CustomReportChannel } from '@/Enums/ReportBuilder/ReportChannel.enum';
import { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import { ICustomReport } from '@/Interfaces/ReportBuilder/CustomReport.interface';
import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import { ICustomReportTemplate, ICustomReportTemplateOption } from '@/Interfaces/ReportBuilder/Template.interface';
import { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { mapGoogleCampaigns } from '@/helpers/google/campaigns';
import { getSortFilter } from '@/helpers/grid/getSortFilter';
import {
  fetchInstantReports,
  fetchScheduledReports,
  fetchTemplate,
  fetchTemplates,
  fetchYoutubeCampaigns,
} from '@/store/api/reportBuilder.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface IState {
  generatedReports: ICustomReport[];
  generatedReportsTotal: number;
  generatedReportsRequestId: string | null;
  scheduledReports: IScheduledCustomReport[];
  scheduledReportsTotal: number;
  scheduledReportsRequestId: string | null;
  campaigns: IControlOption[];
  campaignsRequestId: string | null;
  templates: ICustomReportTemplateOption[];
  templatesRequestId: string | null;
  templateLoadRequestId: string | null;
}

enum Actions {
  GetScheduledReports = 'youtubeReportBuilderSlice/getScheduledReports',
  GetTemplates = 'youtubeReportBuilderSlice/getTemplates',
  GetGeneratedReports = 'youtubeReportBuilderSlice/getGeneratedReports',
  GetCampaigns = 'youtubeReportBuilderSlice/getCampaigns',
  LoadTemplate = 'youtubeReportBuilderSlice/loadTemplate',
}

const initialState: IState = {
  generatedReports: [],
  generatedReportsTotal: 0,
  generatedReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  scheduledReports: [],
  scheduledReportsTotal: 0,
  scheduledReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  campaigns: [],
  campaignsRequestId: DEFAULT_LOADING_REQUEST_ID,
  templates: [],
  templatesRequestId: DEFAULT_LOADING_REQUEST_ID,
  templateLoadRequestId: null,
};

const CUSTOM_REPORT_CHANNEL = CustomReportChannel.Youtube;

export const youtubeReportBuilderSlice = createSlice({
  name: 'youtubeReportBuilder',
  initialState,
  reducers: {
    appendTemplate: (state, action) => {
      state.templates = [action.payload, ...state.templates];
    },
    resetYoutubeReportBuilderState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getCampaigns.pending, pendingCaseHandler('campaigns'))
      .addCase(getCampaigns.rejected, rejectedCaseHandler('campaigns'))
      .addCase(getCampaigns.fulfilled, fulfilledCaseHandler('campaigns'))
      .addCase(getTemplates.pending, pendingCaseHandler('templates'))
      .addCase(getTemplates.rejected, rejectedCaseHandler('templates'))
      .addCase(getTemplates.fulfilled, fulfilledCaseHandler('templates'))
      .addCase(loadTemplate.pending, pendingCaseHandler('templateLoad'))
      .addCase(loadTemplate.rejected, rejectedCaseHandler('templateLoad'))
      .addCase(loadTemplate.fulfilled, (state, action) => {
        if (state.templateLoadRequestId === action.meta.requestId) {
          state.templateLoadRequestId = null;
        }
      })
      .addCase(getGeneratedReports.pending, pendingCaseHandler('generatedReports'))
      .addCase(getGeneratedReports.rejected, rejectedCaseHandler('generatedReports'))
      .addCase(getGeneratedReports.fulfilled, (state, action) => {
        if (state.generatedReportsRequestId === action.meta.requestId) {
          state.generatedReportsRequestId = null;
          state.generatedReports = action.payload?.data ?? [];
          state.generatedReportsTotal = action.payload?.meta?.total ?? 0;
        }
      })
      .addCase(getScheduledReports.pending, pendingCaseHandler('scheduledReports'))
      .addCase(getScheduledReports.rejected, rejectedCaseHandler('scheduledReports'))
      .addCase(getScheduledReports.fulfilled, (state, action) => {
        if (state.scheduledReportsRequestId === action.meta.requestId) {
          state.scheduledReportsRequestId = null;
          state.scheduledReports = action.payload?.data ?? [];
          state.scheduledReportsTotal = action.payload?.meta?.total ?? 0;
        }
      });
  },
});

export const { appendTemplate, resetYoutubeReportBuilderState } = youtubeReportBuilderSlice.actions;

export const getScheduledReports = createAsyncThunk<
  IPaginatedApiResponse<IScheduledCustomReport[]>,
  Partial<ICustomReportListRequestPayload> & IAbortSignalThunkArg
>(Actions.GetScheduledReports, async payload => {
  const { pageSize, page, sort, signal } = payload ?? {};
  const params = {
    limit: pageSize ?? 15,
    page: page ?? 1,
    sort: getSortFilter(sort),
  };

  return await fetchScheduledReports(params, signal, CUSTOM_REPORT_CHANNEL);
});

export const getGeneratedReports = createAsyncThunk<
  IPaginatedApiResponse<ICustomReport[]>,
  Partial<ICustomReportListRequestPayload> & IAbortSignalThunkArg
>(Actions.GetGeneratedReports, async payload => {
  const { pageSize, page, sort, signal } = payload ?? {};
  const params = {
    limit: pageSize ?? 15,
    page: page ?? 1,
    sort: getSortFilter(sort),
  };

  return await fetchInstantReports(params, signal, CUSTOM_REPORT_CHANNEL);
});

export const getCampaigns = createAsyncThunk<
  IControlOption[],
  IAbortSignalThunkArg & {
    advertisers: IControlOption[];
    clients: IClientControlOption[];
  }
>(Actions.GetCampaigns, async payload => {
  const { advertisers, clients, signal } = payload;
  const advertiserIds: number[] = [];

  if (!advertisers?.length) {
    clients.forEach(client => {
      client.advertisers?.forEach(advertiser => advertiserIds.push(advertiser.value));
    });
  } else {
    advertisers.forEach(advertiser => {
      advertiserIds.push(advertiser.value);
    });
  }

  if (!advertiserIds?.length) {
    return [];
  }

  const campaigns = await fetchYoutubeCampaigns(advertiserIds, signal);

  return mapGoogleCampaigns(campaigns);
});

export const getTemplates = createAsyncThunk<ICustomReportTemplateOption[], IAbortSignalThunkArg | undefined>(
  Actions.GetTemplates,
  async payload => {
    return await fetchTemplates(payload?.signal, CUSTOM_REPORT_CHANNEL);
  },
);

export const loadTemplate = createAsyncThunk<
  ICustomReportTemplate,
  Partial<IAbortSignalThunkArg> & { id: ICustomReportTemplate['id'] }
>(Actions.LoadTemplate, async payload => {
  const { signal, id } = payload ?? {};
  return await fetchTemplate(id, signal, CUSTOM_REPORT_CHANNEL);
});

export default youtubeReportBuilderSlice.reducer;
